import type { DiffRow } from '../services/types';

interface DiffTableProps {
  diffs: DiffRow[];
  totalDiffs: number;
  offset: number;
  onPageChange: (offset: number) => void;
  pageSize?: number;
}

const CELL_MAX = 200;

function truncate(value: string | null | undefined): string {
  if (value == null) return '';
  return value.length > CELL_MAX ? value.slice(0, CELL_MAX) + '…' : value;
}

function changeClass(changeType: string): string {
  if (changeType === 'value_nulled') return 'bg-danger/10 text-danger';
  if (changeType === 'value_filled') return 'bg-brand/10 text-brand-dark';
  return 'bg-warn/10 text-warn';
}

export function DiffTable({
  diffs,
  totalDiffs,
  offset,
  onPageChange,
  pageSize = 500,
}: DiffTableProps) {
  if (diffs.length === 0) {
    return (
      <p className="text-sm text-muted py-4">
        No differences found — the cleansed output matches the original for every row.
      </p>
    );
  }

  const hasPrev = offset > 0;
  const hasNext = offset + diffs.length < totalDiffs;

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">
        Showing {offset + 1}–{offset + diffs.length} of {totalDiffs} changed cell
        {totalDiffs === 1 ? '' : 's'}.{' '}
        <span className="inline-block w-3 h-3 rounded-sm bg-warn/20 border border-warn/40 align-middle" />{' '}
        value changed{' '}
        <span className="inline-block w-3 h-3 rounded-sm bg-danger/20 border border-danger/40 align-middle ml-2" />{' '}
        nulled{' '}
        <span className="inline-block w-3 h-3 rounded-sm bg-brand/20 border border-brand/40 align-middle ml-2" />{' '}
        filled
      </p>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-alt text-muted text-xs uppercase">
              <th className="px-3 py-2 text-left font-medium w-16">Row</th>
              <th className="px-3 py-2 text-left font-medium w-32">Column</th>
              <th className="px-3 py-2 text-left font-medium">Original</th>
              <th className="px-3 py-2 text-left font-medium">Cleaned</th>
              <th className="px-3 py-2 text-left font-medium w-28">Change</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {diffs.map((d) => (
              <tr key={`${d.row_id}-${d.column}`} className="hover:bg-surface-alt/40">
                <td className="px-3 py-2 font-mono text-xs text-muted">{d.row_id}</td>
                <td className="px-3 py-2 font-mono text-xs">{d.column}</td>
                <td className="px-3 py-2 text-xs text-muted max-w-xs break-words">
                  {truncate(d.original)}
                </td>
                <td
                  className={`px-3 py-2 text-xs max-w-xs break-words rounded-sm ${changeClass(d.change_type)}`}
                >
                  {d.change_type === 'value_nulled' ? (
                    <em className="opacity-60">null</em>
                  ) : (
                    truncate(d.cleaned)
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-muted">{d.change_type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(hasPrev || hasNext) && (
        <div className="flex gap-3 justify-end text-sm">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(0, offset - pageSize))}
            disabled={!hasPrev}
            className="text-brand-dark hover:underline disabled:text-muted disabled:no-underline"
          >
            ← Previous
          </button>
          <button
            type="button"
            onClick={() => onPageChange(offset + pageSize)}
            disabled={!hasNext}
            className="text-brand-dark hover:underline disabled:text-muted disabled:no-underline"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
